'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

const SITE_KEY =
  process.env.NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_SITE_KEY ||
  'ysc1_K3tNL9H4J9rdIwXtXooRgVVUcGAfDh8iYaT3tyYC4c730f34';

const SCRIPT_SRC =
  'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=onYandexSmartCaptchaLoad';

export const SMARTCAPTCHA_LOAD_TIMEOUT_MS = 10_000;
export const SMARTCAPTCHA_TOKEN_TIMEOUT_MS = 20_000;

type SmartCaptchaApi = {
  render: (
    containerId: string,
    options: {
      sitekey: string;
      invisible: boolean;
      hl?: 'ru' | 'en';
      test?: boolean;
      callback: (token: string) => void;
      hideShield?: boolean;
      shieldPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    }
  ) => number;
  execute: (widgetId?: number) => void;
  reset: (widgetId?: number) => void;
  destroy: (widgetId: number) => void;
  subscribe: (
    widgetId: number,
    event: 'token-expired' | 'network-error' | 'javascript-error' | 'challenge-visible' | 'challenge-hidden',
    callback: () => void,
  ) => () => void;
};

declare global {
  interface Window {
    smartCaptcha?: SmartCaptchaApi;
    onYandexSmartCaptchaLoad?: () => void;
    __yandexSmartCaptchaReady?: Promise<void>;
    __resolveYandexSmartCaptchaReady?: () => void;
    __rejectYandexSmartCaptchaReady?: (error: Error) => void;
  }
}

/** Visible verification for forms inside a Radix modal. */
export function VisibleSmartCaptcha({ onTokenChange, onChallengeChange }: {
  onTokenChange: (token: string) => void;
  onChallengeChange: (visible: boolean) => void;
}) {
  const reactId = useId();
  const containerId = `visible-smart-captcha-${reactId.replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let widgetId: number | null = null;
    const unsubscribers: (() => void)[] = [];
    onTokenChange('');
    setError('');
    setIsReady(false);

    loadSmartCaptchaScript().then(() => {
      const api = window.smartCaptcha;
      const container = containerRef.current;
      if (cancelled || !api || !container) return;

      const testMode = process.env.NODE_ENV !== 'production' &&
        new URLSearchParams(window.location.search).get('captchaTest') === '1';
      widgetId = api.render(containerId, {
        sitekey: SITE_KEY,
        invisible: testMode,
        hl: 'ru',
        test: testMode,
        callback: (token) => {
          if (!cancelled) {
            setError('');
            onTokenChange(token);
          }
        },
      });

      // Leave Yandex's iframe in its original DOM position. Moving a mounted
      // iframe reloads its document and can break the challenge's message bridge.
      unsubscribers.push(api.subscribe(widgetId, 'challenge-visible', () => onChallengeChange(true)));
      unsubscribers.push(api.subscribe(widgetId, 'challenge-hidden', () => onChallengeChange(false)));
      unsubscribers.push(api.subscribe(widgetId, 'token-expired', () => onTokenChange('')));
      const handleError = () => {
        onTokenChange('');
        setError('Не удалось выполнить проверку. Попробуйте ещё раз.');
      };
      unsubscribers.push(api.subscribe(widgetId, 'network-error', handleError));
      unsubscribers.push(api.subscribe(widgetId, 'javascript-error', handleError));
      setIsReady(true);
      if (testMode) api.execute(widgetId);
    }).catch((cause) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : 'Не удалось загрузить капчу.');
    });

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      if (widgetId !== null) window.smartCaptcha?.destroy(widgetId);
    };
  }, [attempt, containerId, onTokenChange, onChallengeChange]);

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600">Подтвердите, что вы не робот</p>
      <div ref={containerRef} id={containerId} className="min-h-[118px]" />
      {!isReady && !error && <p role="status" className="text-sm text-slate-500">Загружаем капчу…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-700">
          {error}{' '}
          <button type="button" className="underline" onClick={() => setAttempt((value) => value + 1)}>
            Повторить проверку
          </button>
        </div>
      )}
    </div>
  );
}

function loadSmartCaptchaScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('SmartCaptcha is available only in browser'));
  }

  if (window.smartCaptcha) {
    return Promise.resolve();
  }

  if (!window.__yandexSmartCaptchaReady) {
    window.__yandexSmartCaptchaReady = new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)?.remove();
        window.__yandexSmartCaptchaReady = undefined;
        window.__resolveYandexSmartCaptchaReady = undefined;
        window.__rejectYandexSmartCaptchaReady = undefined;
        reject(new Error('SmartCaptcha не загрузилась вовремя. Попробуйте ещё раз.'));
      }, SMARTCAPTCHA_LOAD_TIMEOUT_MS);

      window.__resolveYandexSmartCaptchaReady = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      window.__rejectYandexSmartCaptchaReady = (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      };
      window.onYandexSmartCaptchaLoad = window.__resolveYandexSmartCaptchaReady;
    });
  }

  if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.defer = true;
    script.async = true;
    script.onerror = () => {
      window.__rejectYandexSmartCaptchaReady?.(
        new Error('Не удалось загрузить SmartCaptcha. Проверьте соединение и повторите попытку.')
      );
      window.__yandexSmartCaptchaReady = undefined;
      script.remove();
    };
    document.head.appendChild(script);
  }

  return window.__yandexSmartCaptchaReady;
}

export function useInvisibleSmartCaptcha() {
  const reactId = useId();
  const containerId = `smart-captcha-${reactId.replace(/:/g, '')}`;
  const widgetIdRef = useRef<number | null>(null);
  const tokenResolveRef = useRef<((token: string) => void) | null>(null);
  const tokenRejectRef = useRef<((error: Error) => void) | null>(null);
  const tokenTimeoutRef = useRef<number | null>(null);
  const [isReady, setIsReady] = useState(false);

  const clearTokenTimeout = useCallback(() => {
    if (tokenTimeoutRef.current !== null) {
      window.clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = null;
    }
  }, []);

  const renderWidget = useCallback(() => {
    if (!window.smartCaptcha) {
      throw new Error('Капча ещё не загружена');
    }

    if (widgetIdRef.current !== null) {
      return widgetIdRef.current;
    }

    if (!document.getElementById(containerId)) {
      throw new Error('Контейнер капчи не найден');
    }

    widgetIdRef.current = window.smartCaptcha.render(containerId, {
      sitekey: SITE_KEY,
      invisible: true,
      hideShield: true,
      shieldPosition: 'bottom-right',
      callback: (token) => {
        clearTokenTimeout();
        if (token) {
          tokenResolveRef.current?.(token);
        } else {
          tokenRejectRef.current?.(new Error('SmartCaptcha вернула пустой токен. Повторите попытку.'));
        }
        tokenResolveRef.current = null;
        tokenRejectRef.current = null;
      },
    });

    setIsReady(true);
    return widgetIdRef.current;
  }, [clearTokenTimeout, containerId]);

  useEffect(() => {
    loadSmartCaptchaScript()
      .then(() => {
        if (document.getElementById(containerId)) {
          renderWidget();
        }
      })
      .catch((error) => {
        tokenRejectRef.current?.(error);
      });
  }, [containerId, renderWidget]);

  const executeCaptcha = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Капча недоступна в серверном окружении'));
    }

    return new Promise<string>((resolve, reject) => {
      const run = () => {
        if (!window.smartCaptcha) {
          reject(new Error('Капча ещё не загружена'));
          return;
        }

        const widgetId = renderWidget();
        clearTokenTimeout();
        tokenRejectRef.current?.(new Error('Предыдущая проверка SmartCaptcha отменена новой попыткой.'));
        tokenResolveRef.current = resolve;
        tokenRejectRef.current = reject;
        tokenTimeoutRef.current = window.setTimeout(() => {
          tokenResolveRef.current = null;
          tokenRejectRef.current = null;
          tokenTimeoutRef.current = null;
          reject(new Error('SmartCaptcha не ответила вовремя. Повторите попытку.'));
        }, SMARTCAPTCHA_TOKEN_TIMEOUT_MS);
        window.smartCaptcha.execute(widgetId);
      };

      if (isReady) {
        run();
        return;
      }

      loadSmartCaptchaScript()
        .then(() => {
          window.setTimeout(run, 0);
        })
        .catch(reject);
    });
  }, [clearTokenTimeout, isReady, renderWidget]);

  const resetCaptcha = useCallback(() => {
    clearTokenTimeout();
    const widgetId = widgetIdRef.current;
    if (typeof window !== 'undefined' && window.smartCaptcha && widgetId !== null) {
      window.smartCaptcha.reset(widgetId);
    }
  }, [clearTokenTimeout]);

  useEffect(() => () => {
    clearTokenTimeout();
    tokenRejectRef.current?.(new Error('Проверка SmartCaptcha отменена.'));
    tokenResolveRef.current = null;
    tokenRejectRef.current = null;
  }, [clearTokenTimeout]);

  const captchaElement = <div id={containerId} />;

  return {
    captchaElement,
    executeCaptcha,
    resetCaptcha,
    isCaptchaReady: isReady,
  };
}
